import { get_delete_step_url } from '@/utils/urls.ts';
import { useState } from 'react';
import { useEditVersionContext } from '@/context/EditVersionContext/useEditVersionContext.ts';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import { useActiveStep } from '@/layouts/AutomationPageWrapper/hooks/useActiveStep.ts';
import { notifyStepMutationError } from '@/services/stepMutationErrors.ts';
import { useActiveCompositionTarget } from '@/layouts/AutomationPageWrapper/hooks/useActiveCompositionTarget.ts';
import useCompositionOperations from '@/hooks/api/automations/useCompositionOperations.ts';

type deleteStepParams = {
  step_id: string;
};

const useDeleteStep = () => {
  const apiClient = useApiClient();
  const { addToQueue, reportLatestDraftChanged } = useEditVersionContext();
  const [pendingCount, setPendingCount] = useState(0);

  const { stepId, setActiveStep } = useActiveStep();
  const { target, setActiveCompositionTarget } = useActiveCompositionTarget();
  const { mutate: applyComposition, isPending: isCompositionPending } = useCompositionOperations();

  const deleteStep = (params: deleteStepParams) => {
    if (target.scope_kind === 'local_subflow' && target.subflow_id) {
      applyComposition(
        [{ op_type: 'delete_local_subflow_step', subflow_id: target.subflow_id, step_id: params.step_id }],
        {
          onSuccess: () => {
            if (params.step_id === stepId) {
              void setActiveCompositionTarget({
                scope_kind: 'local_subflow',
                subflow_id: target.subflow_id,
              });
            }
          },
        },
      );
      return;
    }
    setPendingCount((count) => count + 1);
    addToQueue({
      mutationFn: async (latestVersion) => {
        const response = await apiClient.put(get_delete_step_url(latestVersion.id), params, {
          suppressGlobalErrorToast: true,
        });
        if (params.step_id === stepId) {
          await setActiveStep(undefined);
        }
        return response.data;
      },
      onSuccess: () => {},
      onError: (error) => {
        notifyStepMutationError('delete step', error, reportLatestDraftChanged);
      },
      onComplete: () => {
        setPendingCount((count) => count - 1);
      },
    });
  };

  return { mutate: deleteStep, isPending: pendingCount > 0 || isCompositionPending };
};

export default useDeleteStep;
